// src/components/map/DeliveryMap.jsx
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import { motion, AnimatePresence } from 'framer-motion';
import { Navigation, Package, MapPin, Maximize2, Minimize2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom Driver Icon (green pulsing)
const createDriverIcon = () => {
  return L.divIcon({
    className: 'driver-marker',
    html: `
      <div class="relative">
        <div class="absolute -inset-2 bg-emerald-400 rounded-full animate-ping opacity-40"></div>
        <div class="relative w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center shadow-lg border-3 border-white">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });
};

// Custom Delivery Icon (orange for available)
const createDeliveryIcon = (isUrgent = false) => {
  const color = isUrgent ? 'from-red-500 to-red-700' : 'from-orange-500 to-orange-700';
  return L.divIcon({
    className: 'delivery-marker',
    html: `
      <div class="w-8 h-8 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center shadow-lg transform -rotate-45">
        <svg class="w-4 h-4 text-white transform rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

// Custom Destination Icon (blue)
const createDestinationIcon = () => {
  return L.divIcon({
    className: 'destination-marker',
    html: `
      <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
        <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Component to update map center when driver position changes
const MapUpdater = ({ position, shouldFollow }) => {
  const map = useMap();

  useEffect(() => {
    if (position && shouldFollow) {
      map.setView(position, map.getZoom(), { animate: true });
    }
  }, [position, shouldFollow, map]);

  return null;
};

const DeliveryMap = ({
  driverPosition,
  availableDeliveries = [],
  currentDelivery = null,
  onDeliveryClick,
  className = '',
  isExpanded = false,
  onToggleExpand
}) => {
  const [followDriver, setFollowDriver] = useState(true);

  // Default center: Dakar, Senegal
  const defaultCenter = [14.6937, -17.4441];
  const center = driverPosition || defaultCenter;

  const handleCenterOnDriver = () => {
    setFollowDriver(true);
  };

  return (
    <motion.div
      className={`relative rounded-2xl overflow-hidden shadow-xl ${className}`}
      layout
      transition={{ duration: 0.3 }}
    >
      {/* Map Controls Overlay */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
        {onToggleExpand && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onToggleExpand}
            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center hover:bg-white transition-colors"
          >
            {isExpanded ? (
              <Minimize2 className="w-5 h-5 text-gray-700" />
            ) : (
              <Maximize2 className="w-5 h-5 text-gray-700" />
            )}
          </motion.button>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCenterOnDriver}
          className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center hover:bg-white transition-colors"
        >
          <Navigation className="w-5 h-5 text-emerald-600" />
        </motion.button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-3">
        <div className="flex flex-col gap-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-500 rounded-full"></div>
            <span className="text-gray-700">Ma position</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-500 rounded-lg transform -rotate-45"></div>
            <span className="text-gray-700">Livraison disponible</span>
          </div>
          {currentDelivery && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span className="text-gray-700">Destination</span>
            </div>
          )}
        </div>
      </div>

      {/* Map Container */}
      <MapContainer
        center={center}
        zoom={14}
        className="w-full h-full"
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapUpdater position={driverPosition} shouldFollow={followDriver} />

        {/* Driver Position with accuracy circle */}
        {driverPosition && (
          <>
            <Circle
              center={driverPosition}
              radius={50}
              pathOptions={{
                color: '#10B981',
                fillColor: '#10B981',
                fillOpacity: 0.1,
                weight: 1
              }}
            />
            <Marker
              position={driverPosition}
              icon={createDriverIcon()}
              eventHandlers={{
                click: () => setFollowDriver(true)
              }}
            >
              <Popup className="custom-popup">
                <div className="text-center">
                  <p className="font-semibold text-emerald-700">Ma position</p>
                  <p className="text-xs text-gray-500">Mise à jour en temps réel</p>
                </div>
              </Popup>
            </Marker>
          </>
        )}

        {/* Available Deliveries */}
        {availableDeliveries.map((delivery) => (
          <Marker
            key={delivery.id}
            position={[
              delivery.delivery_latitude || delivery.from_latitude || 14.6937,
              delivery.delivery_longitude || delivery.from_longitude || -17.4441
            ]}
            icon={createDeliveryIcon(delivery.is_urgent)}
            eventHandlers={{
              click: () => onDeliveryClick && onDeliveryClick(delivery)
            }}
          >
            <Popup>
              <div className="min-w-[200px]">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-orange-500" />
                  <span className="font-semibold">Livraison #{delivery.tracking_number?.slice(-6)}</span>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {delivery.to_address || 'Adresse non spécifiée'}
                </p>
                {delivery.cost && (
                  <p className="text-sm font-medium text-emerald-600">
                    {delivery.cost.toLocaleString()} FCFA
                  </p>
                )}
                <button
                  onClick={() => onDeliveryClick && onDeliveryClick(delivery)}
                  className="mt-2 w-full py-1.5 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  Voir les détails
                </button>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Current Delivery Destination */}
        {currentDelivery && currentDelivery.delivery_latitude && (
          <Marker
            position={[currentDelivery.delivery_latitude, currentDelivery.delivery_longitude]}
            icon={createDestinationIcon()}
          >
            <Popup>
              <div className="min-w-[200px]">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  <span className="font-semibold">Destination</span>
                </div>
                <p className="text-sm text-gray-600">
                  {currentDelivery.to_address || 'Adresse de livraison'}
                </p>
                {currentDelivery.recipient_name && (
                  <p className="text-sm text-gray-500 mt-1">
                    Client: {currentDelivery.recipient_name}
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Loading Overlay */}
      {!driverPosition && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-[1001]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600">Localisation en cours...</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default DeliveryMap;
